import useDocumentTitle from '../hooks/useDocumentTitle';
import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { getApiErrorMessage } from '../lib/errorUtils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import RepPickerDialog from '../components/RepPickerDialog';   // v3.18-1387 CRM-001
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import GhostDealBuilder from '../components/GhostDealBuilder';
import CF360StateBadge from '../components/CF360StateBadge';
import { CF360TransferBadge } from '../components/CF360TransferNotice';
import FinancingResultModal from '../components/FinancingResultModal';   // v3.21-1760
import MonthlyGoalCell from '../components/MonthlyGoalCell';   // v3.21-1798
// v3.15-974: RebalanceVoModal import removed — VO workload UI deleted.
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import ToolbarClock from '../components/ToolbarClock';
import { Plus, Search, Filter, Loader2, Mail, Phone, Building2, DollarSign, Trash2, CalendarIcon, X, Globe, ListOrdered, UserCog } from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfMonth, endOfMonth, subMonths, startOfYear } from 'date-fns';
import {
  deriveClientTimezone,
  tzBucketFor,
  TZ_FILTER_OPTIONS,
} from '../lib/clientTimezone';
// v3.18-1331 — Next Action helper. (v3.18-1333: Rep Assist column removed.)
import { nextActionForClient } from '../lib/nextAction';
// v3.18-1332 — Pipeline Last Activity column helper.
// v3.18-1333 — compact relative time (badge only; actor moved to tooltip).
import {
  LAST_ACTIVITY_BADGE_CLASS,
  formatExactTimestamp,
  compactRelativeTime,
  lastActivityFor,
} from '../lib/lastActivity';

// Simplified CRM Pipeline - single horizontal flow
const stageColors = {
  // v3.18-1332 — Canonical workflow stages (from Deal Stage Engine).
  application_started:      'bg-cyan-100 text-cyan-800',
  application_complete:     'bg-blue-100 text-blue-800',
  submitted_to_lenders:     'bg-amber-100 text-amber-800',
  offers_received:          'bg-yellow-100 text-yellow-800',
  offers_presented:         'bg-indigo-100 text-indigo-800',
  client_approved:          'bg-purple-100 text-purple-800',
  loan_documents_sent:      'bg-violet-100 text-violet-800',
  loan_documents_signed:    'bg-fuchsia-100 text-fuchsia-800',
  funding:                  'bg-lime-100 text-lime-800',
  paid_in_full:             'bg-emerald-100 text-emerald-800',
  closed:                   'bg-slate-100 text-slate-800',
  cancelled:                'bg-red-100 text-red-800',
  // Legacy stages (kept for pre-1332 rows and filter dropdown).
  new_application: 'bg-blue-100 text-blue-800',
  new_lead: 'bg-cyan-100 text-cyan-800',
  proposal_sent: 'bg-indigo-100 text-indigo-800',
  proposal_approved: 'bg-purple-100 text-purple-800',
  contract_sent: 'bg-violet-100 text-violet-800',
  contract_signed: 'bg-fuchsia-100 text-fuchsia-800',
  financing_submitted: 'bg-amber-100 text-amber-800',
  financing_approved: 'bg-lime-100 text-lime-800',
  funded: 'bg-emerald-100 text-emerald-800',
  declined: 'bg-red-100 text-red-800',
  guest_did_not_use_financing: 'bg-gray-100 text-gray-800',
  no_offers_found: 'bg-orange-100 text-orange-800',
  financing_approved_by_client: 'bg-teal-100 text-teal-800',
  bank_needs_docs: 'bg-orange-100 text-orange-800',
  final_review_by_lender: 'bg-pink-100 text-pink-800',
};

// Simplified pipeline stages
const stageOptions = [
  { value: 'new_application', label: 'New Application' },
  { value: 'new_lead', label: 'New Lead' },
  { value: 'proposal_sent', label: 'Proposal Sent' },
  { value: 'proposal_approved', label: 'Proposal Approved' },
  { value: 'contract_sent', label: 'Contract Sent' },
  { value: 'contract_signed', label: 'Contract Signed' },
  { value: 'financing_submitted', label: 'Financing Submitted' },
  { value: 'offers_received', label: 'Offers Received' },
  { value: 'financing_approved', label: 'Financing Approved' },
  { value: 'funded', label: 'Funded' },
  { value: 'declined', label: 'Declined' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'guest_did_not_use_financing', label: 'Guest Did Not Use Financing' },
  { value: 'no_offers_found', label: 'No Offers Found' },
];

const fundingStatusOptions = [
  { value: '', label: 'None' },
  { value: 'pending', label: 'Pending' },
  { value: 'processing', label: 'Processing' },
  { value: 'funded', label: 'Funded' },
  { value: 'on_hold', label: 'On Hold' },
];


// v3.18-1331 — Pipeline "Next Action" cell.
// v3.18-1333 — Use color sparingly. Only ACTION (operator must do
// something) gets a prominent color; WAIT / DONE / DEFAULT stay muted
// so the eye is drawn to rows that need work.
const NEXT_ACTION_TONE_CLASS = {
  action:   'text-blue-700 font-semibold',
  wait:     'text-slate-500',
  schedule: 'text-indigo-700',
  done:     'text-slate-400',
  default:  'text-slate-500',
};
const NEXT_ACTION_DOT_CLASS = {
  action:   'bg-blue-500',
  wait:     'bg-slate-300',
  schedule: 'bg-indigo-500',
  done:     'bg-slate-200',
  default:  'bg-slate-200',
};
const NextActionCell = ({ client }) => {
  const na = nextActionForClient(client);
  return (
    <span className="inline-flex items-center gap-1.5" data-next-action-key={na.key}>
      <span
        className={`inline-block w-2 h-2 rounded-full ${NEXT_ACTION_DOT_CLASS[na.tone] || NEXT_ACTION_DOT_CLASS.default}`}
      />
      <span className={NEXT_ACTION_TONE_CLASS[na.tone] || NEXT_ACTION_TONE_CLASS.default}>
        {na.label}
      </span>
    </span>
  );
};

// v3.21-1610 — Pipeline "Last Activity" cell (two-line layout).
// Line 1: compact elapsed time (2m · 35m · 2h · Yesterday · 4d · No Activity)
// Line 2: readable name of the person who performed the most recent activity.
// Both lines live inside the existing column width — no widening.
const LastActivityCell = ({ client }) => {
  const la      = lastActivityFor(client);
  const label   = compactRelativeTime(la.timestamp);
  const colorCls = LAST_ACTIVITY_BADGE_CLASS[la.color] || LAST_ACTIVITY_BADGE_CLASS.red;
  const exact   = formatExactTimestamp(la.timestamp);
  const tooltip = exact
    ? `${exact}\n${la.activity_description}\n${la.user_name}`
    : `${la.activity_description}\n${la.user_name}`;
  const hasActivity = !!la.timestamp;
  return (
    <span
      className="inline-flex flex-col leading-tight"
      title={tooltip}
      data-last-activity-color={la.color}
      data-testid={`last-activity-cell-${client.id}`}
    >
      <span
        className={`text-sm font-medium ${colorCls}`}
        data-testid={`last-activity-time-${client.id}`}
      >
        {label}
      </span>
      <span
        className="text-xs text-slate-600 mt-0.5 truncate max-w-[160px]"
        data-testid={`last-activity-user-${client.id}`}
      >
        {hasActivity ? la.user_name : ''}
      </span>
    </span>
  );
};


const ClientsPage = () => {
  useDocumentTitle('Clients');
  const navigate = useNavigate();
  const { user, isAdmin, isInternal, authChecked, canEditFinancialGoal } = useAuth();
  const [clients, setClients] = useState([]);
  // v3.18-1360 CLIENT-SEARCH-003 — active employee match (renders the
  // "Clients Connected to <Name>" header when non-null).
  const [matchedEmployee, setMatchedEmployee] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [companyFilter, setCompanyFilter] = useState('all');
  // v3.21-1643 · INTERNAL Financial Summary Strip — server-aggregated
  // Funded / Deal / Approved totals over the current filter set.
  const [finSummary, setFinSummary] = useState(null);
  // v3.21-1827 · HOTFIX for the v3.21-1826 production regression
  // (ReferenceError on `/clients`).  Two layers of safety:
  //
  //   (1) Null-safe synchronous initializer.  ``user`` from
  //       ``useAuth()`` can be ``null`` on the very first render
  //       while the auth payload is still loading.  Optional-chaining
  //       (``user?.can_view_all_clients``) guarantees we never throw
  //       a ReferenceError and never dereference an undefined object.
  //
  //   (2) Async late-apply.  When auth finishes loading AFTER mount
  //       the one-time ``useState`` initializer has already run with
  //       ``user === null`` and the permission-holder would be stuck
  //       on the normal-user defaults.  A ``useEffect`` keyed on
  //       ``authChecked`` + ``user?.can_view_all_clients`` therefore
  //       re-applies the correct defaults EXACTLY ONCE (guarded by
  //       ``defaultsAppliedRef``) when authentication completes and
  //       the user turns out to be a permission-holder.  The ref
  //       guarantees we never override a filter the operator
  //       manually changed afterward.
  const defaultsAppliedRef = useRef(false);
  const [statusFilter, setStatusFilter] = useState(
    user?.can_view_all_clients ? 'all' : 'active'
  );
  // v3.15-952-CLIENTS-TIMEZONE: TZ filter (EST/CST/MST/PST/all) + work-queue
  // view toggle. Both are pure client-side so they pair instantly with the
  // existing search/stage/company/date filters.
  const [tzFilter, setTzFilter] = useState('all');
  // v3.21-1625 · Dedicated Team Member filter — decoupled from the free-
  // text client search box.  Value is a user_id (or 'all').  Server-side
  // filter uses the authoritative ASSIGNMENT_FIELDS registry — no fuzzy
  // name matching against client text.
  const [teamMemberFilter, setTeamMemberFilter] = useState('all');
  // v3.18-1333 — Rep Assist column removed per operator feedback.

  // v3.15-960: VO Officer filter — Timeshare Exit only.
  // Values: 'all' | '__unassigned__' | <vo_officer_id>
  const [voFilter, setVoFilter] = useState('all');
  const [viewMode, setViewMode] = useState('standard'); // 'standard' | 'work_queue'
  // v3.15-1058: Lead-staging — admin-only filter for inactive/staged leads.
  // States: 'hide' (default, active-only) | 'show' (mix) | 'only' (only staged).
  const [inactiveFilter, setInactiveFilter] = useState('hide');
  // v3.15-1104 — CF360 dormant filter toggle. Default ON (include dormant
  // clients in the list per JOINT_HUB_CF360_LIFECYCLE_ALIGNMENT §3.1:
  // dormant clients remain visible & searchable). Operators may toggle
  // OFF to hide dormant clients from the list temporarily.
  const [includeCf360Dormant, setIncludeCf360Dormant] = useState(true);
  // v3.15-1058: Selected client IDs for Bulk Activate action.
  const [selectedInactiveIds, setSelectedInactiveIds] = useState(new Set());
  // v3.18-1387 CRM-001 — Rep transfer state
  const [transferSingle, setTransferSingle] = useState(null); // client obj or null
  const [transferBulk, setTransferBulk]     = useState(false);
  const [bulkActivating, setBulkActivating] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  // v3.21-1760 · Per-Client financing-result modal state. Keyed to the
  // Client being viewed. Opening the modal for one Client does NOT
  // affect any other row in the list, and closing it never cancels
  // upstream Fitnurf processing (backend sync worker + persistence
  // are decoupled from FE lifecycle).
  const [financingView, setFinancingView] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Date filter state - default to current month
  // v3.21-1826 · Date filter default — see v3.21-1827 header comment
  // for the two-layer null-safe + late-apply strategy.
  const [dateFilterType, setDateFilterType] = useState(
    user?.can_view_all_clients ? 'all_time' : 'current_month'
  );
  const [dateRange, setDateRange] = useState({
    from: startOfMonth(new Date()),
    to: endOfMonth(new Date())
  });
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // v3.21-1827 · Late-apply the permission-holder defaults when auth
  // finishes loading AFTER mount.  The ``useState`` initializers above
  // run exactly once with whatever ``user`` is at mount; if auth was
  // still loading at that moment a permission-holder (Cierra class)
  // would otherwise be stuck on the normal-user defaults.  The ref
  // guard guarantees this effect fires at most once, so if the
  // operator manually changes the filter afterward we never override
  // her choice.
  useEffect(() => {
    if (defaultsAppliedRef.current) return;
    if (!authChecked) return;
    if (user?.can_view_all_clients === true) {
      setStatusFilter('all');
      setDateFilterType('all_time');
    }
    defaultsAppliedRef.current = true;
  }, [authChecked, user]);

  // Date filter presets
  const dateFilterPresets = [
    { value: 'current_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'last_3_months', label: 'Last 3 Months' },
    { value: 'this_year', label: 'This Year' },
    { value: 'all_time', label: 'All Time' },
    { value: 'custom', label: 'Custom Range' },
  ];

  // Compute date range based on filter type
  const getDateRangeForFilter = useCallback((filterType) => {
    const now = new Date();
    switch (filterType) {
      case 'current_month':
        return { from: startOfMonth(now), to: endOfMonth(now) };
      case 'last_month':
        const lastMonth = subMonths(now, 1);
        return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
      case 'last_3_months':
        return { from: startOfMonth(subMonths(now, 2)), to: endOfMonth(now) };
      case 'this_year':
        return { from: startOfYear(now), to: endOfMonth(now) };
      case 'all_time':
        return { from: null, to: null };
      case 'custom':
        return dateRange;
      default:
        return { from: startOfMonth(now), to: endOfMonth(now) };
    }
  }, [dateRange]);

  // Handle date filter type change
  const handleDateFilterChange = useCallback((value) => {
    setDateFilterType(value);
    if (value === 'custom') {
      setIsDatePickerOpen(true);
    } else {
      const newRange = getDateRangeForFilter(value);
      setDateRange(newRange);
    }
  }, [getDateRangeForFilter]);

  // Add Client form state - matching all fields from Edit Client
  const [ghostOpen, setGhostOpen] = useState(false);
  const [newClient, setNewClient] = useState({
    // Client Info
    full_name: '',
    email: '',
    phone: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
    // Company & Assignment
    company_id: '',
    director_name: '',
    sales_rep_name: '',
    ptc_rep_id: '',
    // Deal Information
    deal_stage: 'new_lead',
    requested_amount: '',  // Amount Applied For
    deal_amount: '',
    approved_amount: '',
    term: '',
    rate: '',
    payment: '',
    // Funding Progress
    funding_status: '',
    ach_entered: false,
    // Status
    is_declined: false,
    is_cancelled: false,
    // Notes
    external_notes: '',
  });

  const fetchClients = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (stageFilter !== 'all') params.append('deal_stage', stageFilter);
      if (companyFilter !== 'all') params.append('company_id', companyFilter);
      // v3.21-1625 · Team Member dropdown — authoritative assignment filter.
      if (teamMemberFilter && teamMemberFilter !== 'all') {
        params.append('team_member_id', teamMemberFilter);
      }
      params.append('status_filter', statusFilter);
      // v3.15-1058: lead-staging filter (admin-only — non-admins always see active-only).
      if (inactiveFilter === 'show') params.append('include_inactive', 'true');
      else if (inactiveFilter === 'only') params.append('only_inactive', 'true');
      
      // Add date filters
      const effectiveRange = getDateRangeForFilter(dateFilterType);
      if (effectiveRange.from) {
        params.append('date_from', format(effectiveRange.from, 'yyyy-MM-dd'));
      }
      if (effectiveRange.to) {
        params.append('date_to', format(effectiveRange.to, 'yyyy-MM-dd'));
      }

      const response = await api.get(`/clients?${params.toString()}`);
      // v3.14-108: API returns {clients: [...], total: n} format
      setClients(response.data?.clients || response.data || []);
      // v3.18-1360 CLIENT-SEARCH-003 — capture the smart-search employee
      // match so the header can switch to "Clients Connected to <Name>".
      setMatchedEmployee(response.data?.matched_employee || null);
      setError(null);

      // v3.21-1643 · INTERNAL Financial Summary — refetch with the
      // SAME filter set so the strip and the table never drift apart.
      // v3.21-1821 · FINANCIAL REPORTING SCOPE DECOUPLING.
      //
      // The Active/Closed/All working-table filter must NEVER drop
      // eligible historical funded events from the financial summary
      // cards.  A Deal that reached ``deal_stage == "funded"`` (which
      // is exactly when funding occurred) is in the working-table
      // ``closed`` set — leaving it out of the Funded Amount card
      // makes legitimate funded volume disappear when the operator
      // views the Active table.  The strip therefore ALWAYS requests
      // ``status_filter=all`` regardless of the table's filter.
      // (Working-table `params` is untouched — the table remains as
      // useful as before.)
      if (isInternal()) {
        try {
          const stripParams = new URLSearchParams(params);
          stripParams.set('status_filter', 'all');
          const s = await api.get(`/clients/summary/financials?${stripParams.toString()}`);
          setFinSummary(s.data || null);
        } catch (_e) { setFinSummary(null); }
      } else {
        setFinSummary(null);
      }
    } catch (error) {
      console.error('Failed to fetch clients:', error);
      setError('Failed to load clients. Please try again.');
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [search, stageFilter, companyFilter, statusFilter, inactiveFilter, dateFilterType, teamMemberFilter, getDateRangeForFilter, isInternal]);

  useEffect(() => {
    const fetchMeta = async () => {
      try {
        const [companiesRes, usersRes] = await Promise.all([
          api.get('/companies'),
          api.get('/users'),
        ]);
        setCompanies(companiesRes.data);
        setUsers(usersRes.data);

        // Set default company for new client
        if (!isInternal() && user?.company_id) {
          setNewClient((prev) => ({ ...prev, company_id: user.company_id }));
        } else if (companiesRes.data.length > 0) {
          const externalCompany = companiesRes.data.find(c => c.company_type === 'external');
          if (externalCompany) {
            setNewClient((prev) => ({ ...prev, company_id: externalCompany.id }));
          }
        }
      } catch (error) {
        console.error('Failed to fetch metadata:', error);
      }
    };

    fetchMeta();
  }, [isInternal, user]);

  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // v3.15-952-CLIENTS-TIMEZONE: apply the TZ filter client-side. Other
  // filters already happen in fetchClients (server-side); the TZ bucket
  // is derived from client.timezone or state, so it's pure-frontend.
  const displayClients = useMemo(() => {
    let list = clients;
    if (tzFilter !== 'all') {
      list = list.filter((c) => tzBucketFor(deriveClientTimezone(c)) === tzFilter);
    }
    // v3.15-960: VO Officer filter — pure client-side, instant.
    // v3.15-961: when filtering by VO (any value other than 'all'), also
    // scope to timeshare_exit deals so "Unassigned VO" doesn't capture
    // non-TS deals that simply happen to have no VO assigned.
    if (voFilter !== 'all') {
      list = list.filter((c) => {
        const _company = companies.find((co) => co.id === c.company_id);
        const _isTS = c.industry_category === 'timeshare_exit'
          || _company?.industry_category === 'timeshare_exit';
        if (!_isTS) return false;
        if (voFilter === '__unassigned__') return !c.vo_officer_id;
        return c.vo_officer_id === voFilter;
      });
    }
    // v3.15-1104 — CF360 dormant filter toggle. Default ON (dormant
    // clients remain visible per lifecycle §3.1). When toggled OFF,
    // clients with cf360_state === 'in_cf360' are hidden from the list
    // (the badge / banner remain elsewhere; this is purely a list view
    // filter, NOT an exclusion from any aggregate or report).
    if (!includeCf360Dormant) {
      list = list.filter((c) => c.cf360_state !== 'in_cf360');
    }
    return list;
  }, [clients, tzFilter, voFilter, companies, includeCf360Dormant]);

  // v3.15-960: VO Officer filter visibility — Timeshare Exit only.
  const isTimeshareCompany = useCallback((cid) => {
    const c = companies.find((x) => x.id === cid);
    return c?.industry_category === 'timeshare_exit';
  }, [companies]);

  const canSeeVoFilter = useMemo(() => {
    if (!user) return false;
    if (isAdmin?.()) return true;
    if (isInternal?.()) return true;
    if (['company_director', 'company_admin', 'company_manager'].includes(user.role)) return true;
    if (user.is_vo === true || user.role === 'vo_officer') return true;
    return false;
  }, [user, isAdmin, isInternal]);

  const showVoFilter = useMemo(() => {
    if (!canSeeVoFilter) return false;
    // External users: only show when their own company is timeshare_exit.
    if (!isInternal?.()) {
      return isTimeshareCompany(user?.company_id);
    }
    // Internal users: show when a TS company is selected, or any TS company
    // exists in the visible companies list (so an admin can slice by VO).
    if (companyFilter !== 'all') return isTimeshareCompany(companyFilter);
    return companies.some((c) => c.industry_category === 'timeshare_exit');
  }, [canSeeVoFilter, isInternal, isTimeshareCompany, user, companyFilter, companies]);

  // v3.15-960: VO Officer dropdown options. Sourced from the already-loaded
  // users list (is_vo=true) — no extra round-trip needed.
  const voOfficerOptions = useMemo(() => {
    const list = (users || []).filter((u) => u.is_vo === true || u.role === 'vo_officer');
    // If a specific TS company is selected, scope the list to that company.
    if (companyFilter !== 'all') {
      return list.filter((u) => u.company_id === companyFilter);
    }
    return list;
  }, [users, companyFilter]);

  // Reset VO filter when it becomes irrelevant (avoid stale ID filtering).
  useEffect(() => {
    if (!showVoFilter && voFilter !== 'all') setVoFilter('all');
  }, [showVoFilter, voFilter]);

  // v3.15-961: VO workload counts.
  // Count = clients in scope (already respects company/stage/date filters)
  // where all_parties_settled !== true. We intentionally count BEFORE the
  // VO filter is applied so each option shows an absolute workload, not a
  // self-filtered "1".
  const voCounts = useMemo(() => {
    const tzFiltered = tzFilter === 'all'
      ? clients
      : clients.filter((c) => tzBucketFor(deriveClientTimezone(c)) === tzFilter);
    const counts = { __unassigned__: 0, __total__: 0 };
    for (const c of tzFiltered) {
      // SSOT: only `all_parties_settled === true` counts as funded/closed.
      if (c.all_parties_settled === true) continue;
      const _company = companies.find((co) => co.id === c.company_id);
      const _isTS = c.industry_category === 'timeshare_exit'
        || _company?.industry_category === 'timeshare_exit';
      if (!_isTS) continue;
      counts.__total__ += 1;
      const vid = c.vo_officer_id;
      if (!vid) {
        counts.__unassigned__ += 1;
      } else {
        counts[vid] = (counts[vid] || 0) + 1;
      }
    }
    return counts;
  }, [clients, tzFilter, companies]);

  // v3.15-961: Quick-access VO pills — "My VO Pipeline" only renders when
  // the viewer is a VO Officer themselves. Buttons set voFilter directly.
  const myVoId = useMemo(() => {
    if (!user) return null;
    if (user.is_vo === true || user.role === 'vo_officer') return user.id || user.user_id || null;
    return null;
  }, [user]);

  // v3.15-974: Rebalance VO state + scope helpers REMOVED with the
  // VO workload UI block.

  // v3.15-952-WORK-QUEUE-VIEW: build the grouped structure used by the
  // Work Queue view. Section 1 = unassigned (always first), then sales
  // reps sorted by total deal volume (desc).
  const workQueue = useMemo(() => {
    if (viewMode !== 'work_queue') return null;
    const unassigned = [];
    const reps = new Map(); // sales_rep_id → { name, items, total }
    for (const c of displayClients) {
      const repId   = (c.sales_rep_id || '').trim();
      const repName = (c.sales_rep_name || '').trim();
      // v3.15-953: same definition the row uses — assigned if EITHER field
      // is populated. Keeps the section header in sync with the per-row
      // "Claim & Call" visibility.
      if (!repId && !repName) {
        unassigned.push(c);
        continue;
      }
      const groupKey = repId || `name:${repName}`;
      if (!reps.has(groupKey)) {
        reps.set(groupKey, {
          rep_id:   groupKey,
          rep_name: c.sales_rep_name || 'Sales Rep',
          items:    [],
          total:    0,
        });
      }
      const bucket = reps.get(groupKey);
      bucket.items.push(c);
      bucket.total += Number(c.deal_amount || 0);
    }
    const unassignedTotal = unassigned.reduce((acc, c) => acc + Number(c.deal_amount || 0), 0);
    const repGroups = Array.from(reps.values())
      .sort((a, b) => b.total - a.total || a.rep_name.localeCompare(b.rep_name));
    return {
      unassigned: { items: unassigned, total: unassignedTotal, count: unassigned.length },
      reps:       repGroups,
    };
  }, [displayClients, viewMode]);

  // v3.15-952-SMART-TIME: shared row renderer used by both Standard and
  // Work Queue views. Adds a "Best Time" column with a green/gray dot
  // indicating whether NOW is inside the client's preferred call window.
  // v3.15-953-CLAIM-AND-CALL: when a client is unassigned and the viewer
  // can claim it, render claim actions inside the Actions column.
  // v3.15-954-CLAIM-NO-AUTO-CALL: primary action is now "Claim Client" —
  // it ONLY assigns the deal, no dialer launch. The secondary "Call"
  // button is a manual, user-initiated action and never fires
  // automatically. There is no "Calling…" intermediate state.
  // v3.21-1610 · Claim Client button removed. Concierge is now assigned
  // automatically on the first qualifying rep touch (call/sms/email/note/
  // appointment) via the shared `log_activity` hook. There is no manual
  // claim workflow on this screen.


  // v3.18-1333 — Rep Assist column removed. The "Let's Get This Deal"
  // rally workflow still exists elsewhere; no dedicated Pipeline column.


  const renderClientRow = (client) => {
    // v3.18-1331 — "Best Time to Call" replaced by "Next Action" +
    // "Rep Assist". Both cell components below derive their state
    // directly from the client row.
    // v3.15-953: a deal counts as "assigned" if EITHER the id or the
    // human-readable name is populated. This matches what the Sales Rep
    // column shows the user (avoids buttons on rows that visibly have a
    // rep name even if the id field happens to be blank/legacy).
    // v3.21-1610 · Untouched/green treatment now derives from the
    // authoritative Concierge assignment + first-qualifying-touch stamp
    // — never from a passive page view.  A client is "new/untouched"
    // ONLY when they have no Concierge AND no qualifying-touch stamp
    // AND no aggregated last_activity timestamp.  This prevents
    // existing production clients with real activity from being
    // retroactively re-flagged as green.
    const hasConcierge = !!(client.application_concierge_id || client.application_concierge_name);
    const hasQualifyingTouch = !!(client.first_qualifying_touch_at || (client.last_activity && client.last_activity.timestamp));
    const isUntouched = !hasConcierge && !hasQualifyingTouch;
    const openClient = () => {
      navigate(`/clients/${client.id}`);
    };
    // v3.18-1333 — VO column removed from the Pipeline queue.  The
    // per-row Timeshare/VO derivation is no longer needed here; VO
    // remains visible on Deal Ownership / Details / Reports / Ghost.
    return (
      <tr
        key={client.id}
        className={`group border-b border-slate-50 table-row-hover cursor-pointer ${
          selectedClients.includes(client.id) ? 'bg-red-50' :
          isUntouched ? 'bg-emerald-50 animate-pulse ring-2 ring-emerald-400 ring-inset' :
          client.is_inactive ? 'bg-amber-50/40' : 'bg-white'
        }`}
        onClick={openClient}
        data-testid={`client-row-${client.id}`}
        data-new-lead={isUntouched ? 'true' : 'false'}
      >
        {showRowCheckbox && (
          <td className="py-3 px-3" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selectedClients.includes(client.id)}
              onCheckedChange={() => {
                setSelectedClients(prev =>
                  prev.includes(client.id)
                    ? prev.filter(id => id !== client.id)
                    : [...prev, client.id]
                );
              }}
              className="border-slate-300"
              data-testid={`select-client-${client.id}`}
            />
          </td>
        )}
        {/* v3.15-1058: per-row select for bulk activate (admin + only-inactive mode) */}
        {isAdmin?.() && inactiveFilter === 'only' && client.is_inactive && (
          <td className="py-3 px-2" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selectedInactiveIds.has(client.id)}
              onCheckedChange={(checked) => {
                setSelectedInactiveIds(prev => {
                  const next = new Set(prev);
                  if (checked) next.add(client.id); else next.delete(client.id);
                  return next;
                });
              }}
              className="border-amber-400 data-[state=checked]:bg-amber-500"
              data-testid={`select-inactive-${client.id}`}
            />
          </td>
        )}
        <td className="py-3 px-3 min-w-[220px] max-w-[300px]">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-white font-semibold text-sm shrink-0">
              {client.full_name?.charAt(0) || 'C'}
            </div>
            <div className="min-w-0 flex-1">
              <p
                className={
                  isUntouched
                    ? 'font-medium cursor-pointer client-name-flash-green truncate'
                    : 'font-medium text-blue-600 hover:underline cursor-pointer truncate'
                }
                data-testid={`client-name-${client.id}`}
                data-untouched={isUntouched ? '1' : '0'}
                onClick={(e) => {
                  // v3.21-1761 · P0 REGRESSION FIX — Restore canonical
                  // Client-name navigation. The v3.21-1760 change that
                  // hijacked this handler to open FinancingResultModal
                  // removed the operator's normal path to the Client
                  // Detail page and read to production users as
                  // "cannot access client." Financing Result is now
                  // triggered by an explicit control on the APP ID
                  // cell below (only present when a real APP ID
                  // exists). Row-level onClick={openClient} already
                  // navigates on any non-stopPropagation click; this
                  // explicit call preserves behavior if a future
                  // parent handler changes.
                  e.stopPropagation();
                  openClient();
                }}
              >
                {client.full_name}
                {/* v3.15-1104 — CF360 state badge inline next to the name */}
                <span className="ml-1 align-middle inline-block">
                  <CF360StateBadge state={client.cf360_state} sentAt={client.cf360_sent_at} compact />
                  <CF360TransferBadge transfer={client.cf360_transfer} testId={`cf360-transfer-badge-${client.id}`} />
                </span>
                {isUntouched && (
                  <span
                    className="ml-1 align-middle inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-300"
                    data-testid={`client-new-badge-${client.id}`}
                  >
                    New
                  </span>
                )}
                {/* v3.21-1622 · The Client Workspace chip previously
                    added to every row here is removed.  Workspace is
                    now surfaced inside the individual client screen
                    (ClientDetailPage toolbar).  See PRD v3.21-1622. */}
              </p>
              <div className="flex items-center gap-1 text-xs text-slate-500 truncate">
                <Mail className="h-3 w-3 shrink-0" />
                <span className="truncate">{client.email}</span>
              </div>
            </div>
          </div>
        </td>
        {/* v3.18-1333 — App ID (restored).
            v3.21-1761 — APP ID is the explicit Financing-Result
            trigger. Clicking a populated APP ID opens the focused
            Financing Result workspace (FinancingResultModal) for
            THIS row's Client, keyed to `client.id`. A "-" (no
            APP ID) is NEVER clickable — no fabricated application,
            no fallback to arbitrary Client history.
            stopPropagation prevents the row-level
            onClick={openClient} from also navigating to Client
            Detail. The Application-Scoped Financing Result
            correction (proper application-id resolution, offer
            dedup, historical exclusion, per-app refresh) is queued
            under v3.21-1762 and does NOT land in this emergency
            version. */}
        <td className="py-3 px-2" data-testid={`app-id-${client.id}`}>
          {client.credit_app_confirmation_number ? (
            <button
              type="button"
              className="text-[11px] font-mono bg-amber-50 text-amber-800 hover:bg-amber-100 hover:text-amber-900 px-1.5 py-0.5 rounded whitespace-nowrap cursor-pointer border border-transparent hover:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-300"
              onClick={(e) => {
                e.stopPropagation();
                setFinancingView({ id: client.id, name: client.full_name });
              }}
              data-testid={`financing-result-trigger-${client.id}`}
              title="Open Financing Result"
              aria-label={`Open Financing Result for ${client.full_name}`}
            >
              {client.credit_app_confirmation_number}
            </button>
          ) : (
            <span className="text-xs text-slate-400" data-testid={`financing-result-unavailable-${client.id}`}>-</span>
          )}
        </td>
        {/* v3.18-1333 — Company (restored, internal-only view; external
            users only see their own company anyway). */}
        {isInternal() && (
          <td className="py-3 px-3 text-[13px] text-slate-600 max-w-[180px] truncate" title={client.company_name || ''} data-testid={`company-${client.id}`}>
            {client.cf360_transfer?.kind === 'company_bucket' ? (
              <span className="font-semibold text-orange-800" data-testid={`company-cf360-${client.id}`}>
                {client.company_name || 'CreditForward360.com'}
                {client.cf360_transfer.original_company_name && (
                  <span className="block text-[10px] font-normal text-slate-500 truncate">from {client.cf360_transfer.original_company_name}</span>
                )}
              </span>
            ) : (client.company_name || <span className="text-slate-400">—</span>)}
          </td>
        )}
        {/* v3.18-1332 — Approved amount. */}
        <td className="py-3 px-3 text-[13px] font-medium text-green-600 whitespace-nowrap" data-testid={`approved-amount-${client.id}`}>
          {client.approved_amount ? `$${client.approved_amount.toLocaleString()}` : '-'}
        </td>
        {/* v3.18-1333 — Deal Amount (restored). */}
        <td className="py-3 px-3 text-[13px] font-medium text-slate-800 whitespace-nowrap" data-testid={`deal-amount-${client.id}`}>
          {client.deal_amount ? `$${client.deal_amount.toLocaleString()}` : '-'}
        </td>
        {/* v3.18-1332 — Sales Rep column. */}
        <td className="py-3 px-3 text-[13px] text-slate-600 max-w-[140px] truncate" title={client.sales_rep_name || ''} data-testid={`sales-rep-${client.id}`}>
          {client.sales_rep_name || (
            <span className="text-amber-700 font-medium">Unassigned</span>
          )}
        </td>
        {/* v3.18-1333 — VO column removed from Pipeline queue.  The
            assigned VO Officer is still populated on the deal
            (`vo_officer_id` / `vo_officer_name`) and continues to
            appear in Deal Ownership, Deal Details, Reports, Ghost, and
            Audit History. No backend functionality removed. */}
        {/* v3.21-1639 — Stage column: Deal Stages SSOT via backend
            workflow resolver (`workflow_current_stage_label`).  Falls
            back to the legacy engine-derived stage only when the
            resolver produced no answer. */}
        <td className="py-3 px-3">
          <Badge
            className={`${stageColors[client.deal_stage] || 'bg-slate-100 text-slate-800'} text-[10px] whitespace-nowrap`}
            variant="secondary"
            title={client.deal_stage_raw && client.deal_stage_raw !== client.deal_stage
              ? `Persisted stage: ${client.deal_stage_raw}`
              : undefined}
            data-testid={`deal-stage-badge-${client.id}`}
          >
            {client.workflow_current_stage_label
              || client.deal_stage_label
              || (client.deal_stage || '').replace(/_/g, ' ')}
          </Badge>
          {/* v3.15-1058: visual indicator for staged/inactive leads (admins only — non-admins don't see them) */}
          {client.is_inactive && (
            <span
              className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-slate-200 text-slate-600 border border-slate-300"
              title="Lead is in INACTIVE/staging state — hidden from reps, dialer, queues, KPIs"
              data-testid={`inactive-badge-${client.id}`}
            >
              Inactive
            </span>
          )}
        </td>
        {/* v3.18-1333 — Next Action.  Never duplicates Stage — see
            ``nextActionForClient`` in ../lib/nextAction.js. */}
        <td className="py-3 px-2 text-[13px] max-w-[160px]" data-testid={`next-action-${client.id}`}>
          <NextActionCell client={client} />
        </td>
        {/* v3.18-1333 — Last Activity (compact: 2m · 35m · 2h ·
            Yesterday · 4d; actor + activity in tooltip). */}
        <td className="py-3 px-2 whitespace-nowrap" data-testid={`last-activity-${client.id}`}>
          <LastActivityCell client={client} />
        </td>
        {showActionsColumn && (
          <td className={`py-3 px-3 text-right sticky right-0 z-[1] shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.08)] ${
            client.is_inactive ? 'bg-amber-50/40' : 'bg-white'
          } group-hover:bg-slate-50`}>
            <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
              {/* v3.21-1610 · Claim Client button removed — Concierge is
                  auto-assigned on the first qualifying rep touch. */}
              {canTransferRep && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  title="Transfer this client to a different sales rep"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTransferSingle(client);
                  }}
                  data-testid={`transfer-rep-${client.id}`}
                >
                  <UserCog className="h-4 w-4" />
                </Button>
              )}
              {canDeleteClient && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    setClientToDelete(client);
                  }}
                  data-testid={`delete-client-${client.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </td>
        )}
      </tr>
    );
  };

  // Get PTC reps (internal users only) for PTC Rep dropdown
  const ptcReps = useMemo(() => {
    return users.filter((u) => u.company_type === 'internal');
  }, [users]);

  // Filter to show only external companies for client creation
  const externalCompanies = useMemo(() => {
    return companies.filter(c => c.company_type === 'external');
  }, [companies]);

  const resetForm = () => {
    const defaultCompanyId = isInternal() 
      ? (externalCompanies[0]?.id || '')
      : (user?.company_id || '');
    
    setNewClient({
      full_name: '',
      email: '',
      phone: '',
      street_address: '',
      city: '',
      state: '',
      zip_code: '',
      company_id: defaultCompanyId,
      director_name: '',
      sales_rep_name: '',
      ptc_rep_id: '',
      deal_stage: 'new_lead',
      requested_amount: '',
      deal_amount: '',
      approved_amount: '',
      term: '',
      rate: '',
      payment: '',
      funding_status: '',
      ach_entered: false,
      is_declined: false,
      is_cancelled: false,
      external_notes: '',
    });
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setCreating(true);

    try {
      const payload = {
        full_name: newClient.full_name,
        email: newClient.email,
        phone: newClient.phone,
        street_address: newClient.street_address || null,
        city: newClient.city || null,
        state: newClient.state || null,
        zip_code: newClient.zip_code || null,
        company_id: newClient.company_id,
        director_name: newClient.director_name || null,
        sales_rep_name: newClient.sales_rep_name || null,
        ptc_rep_id: newClient.ptc_rep_id || null,
        deal_stage: newClient.deal_stage,
        requested_amount: newClient.requested_amount ? parseFloat(newClient.requested_amount) : null,
        deal_amount: newClient.deal_amount ? parseFloat(newClient.deal_amount) : null,
        approved_amount: newClient.approved_amount ? parseFloat(newClient.approved_amount) : null,
        term: newClient.term || null,
        rate: newClient.rate ? parseFloat(newClient.rate) : null,
        payment: newClient.payment ? parseFloat(newClient.payment) : null,
        funding_status: newClient.funding_status || null,
        ach_entered: newClient.ach_entered,
        is_declined: newClient.is_declined,
        is_cancelled: newClient.is_cancelled,
        external_notes: newClient.external_notes || '',
      };
      
      const _clientRes = await api.post('/clients', payload);
      toast.success('Client created successfully');
      setIsCreateOpen(false);
      resetForm();
      fetchClients();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to create client'));
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`/clients/${clientToDelete.id}`);
      toast.success('Client deleted successfully');
      setClientToDelete(null);
      setSelectedClients(prev => prev.filter(id => id !== clientToDelete.id));
      fetchClients();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete client'));
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedClients.length === 0) return;
    
    setBulkDeleting(true);
    try {
      await api.post('/clients/bulk-delete', { client_ids: selectedClients });
      toast.success(`${selectedClients.length} client(s) deleted successfully`);
      setSelectedClients([]);
      setShowBulkDeleteDialog(false);
      fetchClients();
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to delete clients'));
    } finally {
      setBulkDeleting(false);
    }
  };

  const toggleSelectClient = (clientId, e) => {
    e.stopPropagation();
    setSelectedClients(prev => 
      prev.includes(clientId) 
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients.map(c => c.id));
    }
  };

  // Check if user can delete clients (PTC Admin and PTC Team Members only)
  // ==========================================================================
  // DO NOT REMOVE — CRM-001 lead-transfer feature.
  // Required for all internal roles per canTransferRep gate.
  // Wiped by unrelated sprints multiple times (v3.18-1355, v3.18-1379,
  // v3.18-1389). Re-added at v3.19-1404 with this guard-comment.
  // Touching: Actions column, UserCog row button, bulk "Transfer Rep"
  //           button, RepPickerDialog mounts (single + bulk),
  //           canTransferRep predicate, showActionsColumn predicate.
  // If any of the above disappears from ClientsPage.js it is a regression.
  // ==========================================================================
  // v3.18-1392 CRM-001-FOLLOWUP — gated on `authChecked` so this can't
  // momentarily evaluate false (hiding the Actions column) while
  // AuthContext's /auth/me fetch is still in flight on first mount.
  // ClientsPage's own client-list fetch has no such gate and can resolve
  // first, rendering a full client list against a still-null user.
  const canDeleteClient = authChecked && isInternal() && (isAdmin() || user?.role === 'ptc_team_member');

  // v3.18-1390 CRM-001 — Transfer Rep visibility.  Any internal ops user
  // who can reassign work (Admins, Directors, Managers, Team Members)
  // sees the row-level UserCog button, the Actions column, the bulk
  // checkboxes, and the bulk "Transfer Rep" button.  Externals never see
  // any of it.
  const canTransferRep = authChecked && isInternal() && (
    isAdmin()
    || user?.role === 'ptc_team_member'
    || user?.role === 'ptc_director'
    || user?.role === 'ptc_manager'
  );

  // v3.18-1390 CRM-001 — Actions column and per-row checkboxes render if
  // the user has ANY action available (delete or rep-transfer).  Before
  // this change, `canDeleteClient` alone controlled the entire column,
  // which meant Managers/Directors could never see the Transfer Rep
  // button even though the backend allowed the transfer.
  const showActionsColumn = canDeleteClient || canTransferRep;
  const showRowCheckbox   = canDeleteClient || canTransferRep;

  return (
    <div className="space-y-4 min-w-0 max-w-full" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          {matchedEmployee ? (
            <>
              {/* v3.18-1360 CLIENT-SEARCH-003 — contextual header when an
                  internal employee matched the search string. */}
              <h1
                className="font-heading text-2xl font-bold text-primary"
                data-testid="matched-employee-header"
              >
                Clients Connected to {matchedEmployee.full_name}
              </h1>
              <p className="text-sm text-slate-500 mt-1" data-testid="matched-employee-count">
                {clients.length} Result{clients.length === 1 ? '' : 's'}
                {matchedEmployee.email ? ` · ${matchedEmployee.email}` : ''}
              </p>
            </>
          ) : (
            <>
              <h1 className="font-heading text-2xl font-bold text-primary">Clients</h1>
              <p className="text-sm text-slate-500 mt-1">
                {isInternal() ? 'Manage all client deals' : 'Manage your client portfolio'}
              </p>
            </>
          )}
        </div>
        <div className="flex gap-2">
          {isInternal() && (
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setGhostOpen(true)} data-testid="ghost-deal-btn">
            <Plus className="h-4 w-4 mr-2" />
            Ghost Deal Builder
          </Button>
          )}
          <Dialog open={isCreateOpen} onOpenChange={(open) => {
          setIsCreateOpen(open);
          if (!open) resetForm();
        }}>
          <DialogTrigger asChild>
            <Button className="bg-accent hover:bg-gold-dark" data-testid="create-client-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Client
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">Create New Client</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateClient} className="space-y-6 mt-4">
              {/* Client Information Section */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Client Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Full Name *</Label>
                    <Input
                      value={newClient.full_name}
                      onChange={(e) => setNewClient({ ...newClient, full_name: e.target.value })}
                      required
                      data-testid="client-name-input"
                    />
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                      required
                      data-testid="client-email-input"
                    />
                  </div>
                  <div>
                    <Label>Phone *</Label>
                    <Input
                      value={newClient.phone}
                      onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                      required
                      data-testid="client-phone-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Street Address</Label>
                    <Input
                      value={newClient.street_address}
                      onChange={(e) => setNewClient({ ...newClient, street_address: e.target.value })}
                      placeholder="123 Main Street, Apt 4B"
                      data-testid="client-street-address"
                    />
                  </div>
                  <div>
                    <Label>City</Label>
                    <Input
                      value={newClient.city}
                      onChange={(e) => setNewClient({ ...newClient, city: e.target.value })}
                      placeholder="Los Angeles"
                      data-testid="client-city"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label>State</Label>
                      <Input
                        value={newClient.state}
                        onChange={(e) => setNewClient({ ...newClient, state: e.target.value })}
                        placeholder="CA"
                        data-testid="client-state"
                      />
                    </div>
                    <div>
                      <Label>Zip Code</Label>
                      <Input
                        value={newClient.zip_code}
                        onChange={(e) => setNewClient({ ...newClient, zip_code: e.target.value })}
                        placeholder="90210"
                        data-testid="client-zip-code"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Assignment Section */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Assignment</h3>
                <div className="grid grid-cols-2 gap-4">
                  {isInternal() && (
                    <div>
                      <Label>Company *</Label>
                      <Select
                        value={newClient.company_id}
                        onValueChange={(value) => setNewClient({ ...newClient, company_id: value })}
                      >
                        <SelectTrigger data-testid="client-company-select">
                          <SelectValue placeholder="Select company" />
                        </SelectTrigger>
                        <SelectContent>
                          {externalCompanies.map((company) => (
                            <SelectItem key={company.id} value={company.id}>
                              {company.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {/* v3.15-1120: Company-driven schema swap. When the
                          selected company is a Business Funding partner,
                          the standard client intake schema does not apply
                          — redirect the operator to the dedicated BF
                          dashboard with the company pre-selected. */}
                      {(() => {
                        const _selectedCompany = companies.find((co) => co.id === newClient.company_id);
                        const _isBF = _selectedCompany?.industry_category === 'business_funding'
                                   || _selectedCompany?.company_type === 'business_funding';
                        if (!_isBF) return null;
                        return (
                          <div
                            className="mt-2 p-3 rounded border border-indigo-300 bg-indigo-50 text-xs text-indigo-800"
                            data-testid="bf-schema-swap-notice"
                          >
                            <div className="font-semibold mb-1">Business Funding partner detected</div>
                            <div className="mb-2">
                              {_selectedCompany?.name} uses the Business Funding intake schema (Application ID, business info, etc.) — the standard client form does not apply here.
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                              data-testid="bf-schema-swap-redirect-btn"
                              onClick={() => {
                                setIsCreateOpen(false);
                                navigate(`/business-funding?company_id=${encodeURIComponent(_selectedCompany.id)}&new=1`);
                              }}
                            >
                              Switch to Business Funding Form →
                            </Button>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                  <div>
                    <Label>Director</Label>
                    <Input
                      placeholder="Enter director name"
                      value={newClient.director_name}
                      onChange={(e) => setNewClient({ ...newClient, director_name: e.target.value })}
                      data-testid="client-director-input"
                    />
                  </div>
                  <div>
                    <Label>Sales Rep</Label>
                    <Input
                      placeholder="Enter sales rep name"
                      value={newClient.sales_rep_name}
                      onChange={(e) => setNewClient({ ...newClient, sales_rep_name: e.target.value })}
                      data-testid="client-sales-rep-input"
                    />
                  </div>
                  <div>
                    <Label>PTC Rep</Label>
                    <Select
                      value={newClient.ptc_rep_id || 'unassigned'}
                      onValueChange={(value) => setNewClient({ ...newClient, ptc_rep_id: value === 'unassigned' ? '' : value })}
                    >
                      <SelectTrigger data-testid="client-ptc-rep-select">
                        <SelectValue placeholder="Select PTC rep" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {ptcReps.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.full_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Deal Information Section */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Deal Information</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Deal Stage</Label>
                    <Select
                      value={newClient.deal_stage}
                      onValueChange={(value) => setNewClient({ ...newClient, deal_stage: value })}
                    >
                      <SelectTrigger data-testid="client-stage-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {stageOptions.map((stage) => (
                          <SelectItem key={stage.value} value={stage.value}>
                            {stage.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Amount Applied For</Label>
                    <Input
                      type="number"
                      value={newClient.requested_amount}
                      onChange={(e) => setNewClient({ ...newClient, requested_amount: e.target.value })}
                      placeholder="0.00"
                      data-testid="client-requested-amount-input"
                    />
                  </div>
                  <div>
                    <Label>Deal Amount</Label>
                    <Input
                      type="number"
                      value={newClient.deal_amount}
                      onChange={(e) => setNewClient({ ...newClient, deal_amount: e.target.value })}
                      placeholder="0.00"
                      data-testid="client-deal-amount-input"
                    />
                  </div>
                  <div>
                    <Label>Approved Amount</Label>
                    <Input
                      type="number"
                      value={newClient.approved_amount}
                      onChange={(e) => setNewClient({ ...newClient, approved_amount: e.target.value })}
                      placeholder="0.00"
                      data-testid="client-approved-amount-input"
                    />
                  </div>
                  <div>
                    <Label>Term</Label>
                    <Input
                      value={newClient.term}
                      onChange={(e) => setNewClient({ ...newClient, term: e.target.value })}
                      placeholder="e.g., 12 months"
                      data-testid="client-term-input"
                    />
                  </div>
                  <div>
                    <Label>Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={newClient.rate}
                      onChange={(e) => setNewClient({ ...newClient, rate: e.target.value })}
                      placeholder="0.00"
                      data-testid="client-rate-input"
                    />
                  </div>
                  <div>
                    <Label>Payment</Label>
                    <Input
                      type="number"
                      value={newClient.payment}
                      onChange={(e) => setNewClient({ ...newClient, payment: e.target.value })}
                      placeholder="0.00"
                      data-testid="client-payment-input"
                    />
                  </div>
                </div>
              </div>

              {/* Funding Progress Section */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Funding Progress</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Funding Status</Label>
                    <Select
                      value={newClient.funding_status || 'none'}
                      onValueChange={(value) => setNewClient({ ...newClient, funding_status: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger data-testid="client-funding-status-select">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        {fundingStatusOptions.map((opt) => (
                          <SelectItem key={opt.value || 'none'} value={opt.value || 'none'}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-4 pt-6">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="ach_entered"
                        checked={newClient.ach_entered}
                        onCheckedChange={(checked) => setNewClient({ ...newClient, ach_entered: checked })}
                        data-testid="client-ach-entered"
                      />
                      <Label htmlFor="ach_entered" className="cursor-pointer">ACH Entered</Label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Status Section */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Status</h3>
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="is_declined"
                      checked={newClient.is_declined}
                      onCheckedChange={(checked) => setNewClient({ ...newClient, is_declined: checked })}
                      data-testid="client-is-declined"
                    />
                    <Label htmlFor="is_declined" className="cursor-pointer text-red-600">Declined</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="is_cancelled"
                      checked={newClient.is_cancelled}
                      onCheckedChange={(checked) => setNewClient({ ...newClient, is_cancelled: checked })}
                      data-testid="client-is-cancelled"
                    />
                    <Label htmlFor="is_cancelled" className="cursor-pointer text-amber-600">Cancelled</Label>
                  </div>
                </div>
              </div>

              {/* Notes Section */}
              <div>
                <h3 className="text-sm font-semibold text-slate-700 mb-3">Notes</h3>
                <div>
                  <Label>External Notes</Label>
                  <Input
                    value={newClient.external_notes}
                    onChange={(e) => setNewClient({ ...newClient, external_notes: e.target.value })}
                    placeholder="Notes visible to all..."
                    data-testid="client-notes-input"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsCreateOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-accent hover:bg-gold-dark"
                  disabled={creating}
                  data-testid="submit-client-btn"
                >
                  {creating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Client'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
        <GhostDealBuilder open={ghostOpen} onClose={() => setGhostOpen(false)} onDealCreated={() => fetchClients()} />
      </div>

      {/* v3.21-1643 · INTERNAL Financial Summary Strip
          v3.21-1798 · Reorganised into the requested three-card
          layout:
            LEFT   — Funded Amount + `<N> deals in scope`
                     (unchanged calculation).
            CENTER — Deal Amount + Approved Amount stacked inside
                     the SAME centre card (no card growth; both
                     underlying totals unchanged).
            RIGHT  — Monthly Funded Goal (v1797 SSOT, keyed by
                     `scope="clients_internal_summary"`, current
                     calendar year+month).  Displays Goal /
                     Remaining or Goal-Exceeded-By / % of Goal
                     inline; pencil-edit stays compact so card
                     dimensions do not change.  Editing is gated
                     server-side by `require_admin`.
          Same 3-column grid, same card widths/heights/shape. */}
      {isInternal() && finSummary && (
        <Card className="mb-4 border-emerald-200 bg-gradient-to-r from-emerald-50 to-white" data-testid="clients-financial-summary">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-lg bg-white border border-slate-200 p-3">
                <div className="text-[11px] font-semibold uppercase text-slate-500">Funded Amount</div>
                <div className="text-2xl font-bold text-emerald-700 mt-1" data-testid="fin-funded-total">
                  ${Number(finSummary.funded_amount_total || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </div>
                <div className="text-[11px] text-slate-500 mt-1">{finSummary.deal_count} deal{finSummary.deal_count === 1 ? '' : 's'} in scope</div>
              </div>
              <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="fin-center-card">
                {/* v3.21-1798 · Deal Amount + Approved Amount stacked
                    inside the existing centre card.  Both underlying
                    calculations unchanged (`deal_amount_total` /
                    `approved_amount_total` sourced from the same
                    finSummary payload). */}
                <div>
                  <div className="text-[11px] font-semibold uppercase text-slate-500">Deal Amount</div>
                  <div className="text-lg font-bold text-slate-900 mt-0.5" data-testid="fin-deal-total">
                    ${Number(finSummary.deal_amount_total || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </div>
                </div>
                <div className="mt-1.5 pt-1.5 border-t border-slate-100">
                  <div className="text-[11px] font-semibold uppercase text-slate-500">Approved Amount</div>
                  <div className="text-lg font-bold text-blue-700 mt-0.5" data-testid="fin-approved-total">
                    ${Number(finSummary.approved_amount_total || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                  </div>
                </div>
              </div>
              {/* v3.21-1799 · Monthly Goal reporting period is
                  derived from the SAME `effectiveRange` that drives
                  finSummary (funded / deal / approved totals).  If
                  the range collapses to a single calendar month we
                  use that month; multi-month ranges render a
                  read-only "not applicable" note.  The edit
                  affordance is gated by `canEditFinancialGoal()`,
                  which mirrors the backend `require_admin` guard on
                  PUT /api/monthly-goal exactly. */}
              <div className="rounded-lg bg-white border border-slate-200 p-3" data-testid="fin-monthly-goal-card">
                {(() => {
                  const r = getDateRangeForFilter(dateFilterType);
                  const from = r?.from || null;
                  const to   = r?.to   || null;
                  const singleMonth =
                    !!from && !!to &&
                    from.getFullYear() === to.getFullYear() &&
                    from.getMonth()    === to.getMonth();
                  const multi = !singleMonth;
                  // When single-month, from/to share year+month.
                  // When multi-month, year/month are irrelevant — the
                  // cell skips the fetch entirely.
                  const y = singleMonth ? from.getFullYear() : 0;
                  const m = singleMonth ? from.getMonth() + 1 : 0;
                  return (
                    <MonthlyGoalCell
                      scope="clients_internal_summary"
                      year={y}
                      month={m}
                      fundedAmount={Number(finSummary.funded_amount_total || 0)}
                      multiMonth={multi}
                      canEdit={canEditFinancialGoal()}
                    />
                  );
                })()}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {/* Row 1: Search and Date Filter */}
            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
              {/* v3.18-1359 CLOCK-001 — Search retains flex-1 but is capped
                  so the middle spacer can host the live clock in the empty
                  toolbar whitespace on desktop. On mobile / small tablet
                  the flex direction stacks; clock hides at <md. */}
              <div className="relative flex-1 min-w-0 sm:max-w-2xl">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search by name, email, phone, or App ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>

              {/* v3.21-1625 · Team Member filter — dedicated dropdown next
                  to Client Search.  Filters clients by the AUTHORITATIVE
                  ASSIGNMENT_FIELDS registry (sales rep / assigned rep /
                  presenter / concierge / EQF director-manager / …).  The
                  dropdown is populated from the same `users` list the
                  server already returns to this page (respecting the
                  operator's normal user visibility).  Inactive users are
                  hidden. */}
              <div className="w-full sm:w-52 shrink-0" data-testid="team-member-filter-wrap">
                <Select value={teamMemberFilter} onValueChange={setTeamMemberFilter}>
                  <SelectTrigger className="w-full" data-testid="team-member-filter">
                    <SelectValue placeholder="All Team Members" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all" data-testid="team-member-opt-all">All Team Members</SelectItem>
                    {[...(users || [])]
                      .filter(u => u && u.id && u.is_active !== false && u.user_status !== 'inactive')
                      .sort((a, b) => (a.full_name || a.email || '').localeCompare(b.full_name || b.email || ''))
                      .map(u => (
                        <SelectItem
                          key={u.id}
                          value={u.id}
                          data-testid={`team-member-opt-${u.id}`}
                        >
                          {u.full_name || u.email}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {/* v3.18-1359 CLOCK-001 — Live clock centered in the toolbar
                  whitespace between the search box and the date controls.
                  flex-1 lets it fluidly claim exactly the leftover width;
                  the clock content itself is fixed and won't stretch. */}
              <div
                className="hidden md:flex flex-1 items-center justify-center min-w-0"
                data-testid="toolbar-clock-slot"
              >
                <ToolbarClock user={user} />
              </div>
              
              {/* Date Filter */}
              <div className="flex items-center gap-2">
                <Select value={dateFilterType} onValueChange={handleDateFilterChange}>
                  <SelectTrigger className="w-full sm:w-40" data-testid="date-filter">
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Date Range" />
                  </SelectTrigger>
                  <SelectContent>
                    {dateFilterPresets.map((preset) => (
                      <SelectItem key={preset.value} value={preset.value}>
                        {preset.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                {/* Custom Date Range Picker */}
                {dateFilterType === 'custom' && (
                  <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-auto justify-start text-left font-normal"
                        data-testid="custom-date-picker-trigger"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {dateRange.from ? (
                          dateRange.to ? (
                            <>
                              {format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d, yyyy")}
                            </>
                          ) : (
                            format(dateRange.from, "MMM d, yyyy")
                          )
                        ) : (
                          <span>Pick dates</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="range"
                        selected={dateRange}
                        onSelect={(range) => {
                          if (range) {
                            setDateRange(range);
                          }
                        }}
                        numberOfMonths={2}
                        data-testid="date-range-calendar"
                      />
                      <div className="flex items-center justify-between p-3 border-t">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setDateRange({ from: null, to: null });
                          }}
                        >
                          Clear
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => setIsDatePickerOpen(false)}
                          disabled={!dateRange.from || !dateRange.to}
                        >
                          Apply
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
                
                {/* Show active date range indicator */}
                {dateFilterType !== 'all_time' && (
                  <div className="hidden sm:flex items-center text-xs text-slate-500">
                    {(() => {
                      const range = getDateRangeForFilter(dateFilterType);
                      if (range.from && range.to) {
                        return `${format(range.from, "MMM d")} - ${format(range.to, "MMM d, yyyy")}`;
                      }
                      return null;
                    })()}
                  </div>
                )}
              </div>
            </div>
            
            {/* Row 2: Status Toggle + Stage and Company Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Status Toggle — Active / Closed / All */}
              <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50" data-testid="status-filter-toggle">
                {[
                  { value: 'active', label: 'Active' },
                  { value: 'closed', label: 'Closed' },
                  { value: 'all', label: 'All' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setStatusFilter(opt.value)}
                    className={`px-3.5 py-1.5 text-sm font-medium rounded-md transition-all ${
                      statusFilter === opt.value
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid={`status-filter-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* v3.15-1058: Admin-only Lead-Staging filter */}
              {isAdmin?.() && (
                <div
                  className={`inline-flex rounded-lg border p-0.5 ${inactiveFilter === 'only' ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}
                  data-testid="inactive-filter-toggle"
                >
                  {[
                    { value: 'hide', label: 'Hide Inactive' },
                    { value: 'show', label: 'Show All' },
                    { value: 'only', label: 'Only Inactive' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setInactiveFilter(opt.value);
                        setSelectedInactiveIds(new Set());
                      }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                        inactiveFilter === opt.value
                          ? (opt.value === 'only' ? 'bg-amber-200 text-amber-900 shadow-sm' : 'bg-white text-slate-900 shadow-sm')
                          : 'text-slate-500 hover:text-slate-700'
                      }`}
                      data-testid={`inactive-filter-${opt.value}`}
                      title={opt.value === 'hide' ? 'Default — hides staged leads from list' : opt.value === 'show' ? 'Show both active + staged leads' : 'Show ONLY staged leads (bulk-activate available)'}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}

              {/* v3.15-1104 — CF360 dormant filter toggle. Default ON.
                  Visible to everyone (dormant clients are not secret —
                  the badge appears regardless). Lifecycle §3.1 keeps
                  clients visible/searchable during dormancy. */}
              <label
                data-testid="cf360-dormant-filter-toggle"
                className={`inline-flex items-center gap-1.5 cursor-pointer rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
                  includeCf360Dormant
                    ? 'border-slate-200 bg-slate-50 text-slate-700'
                    : 'border-amber-300 bg-amber-50 text-amber-900'
                }`}
                title="Toggle visibility of clients currently in CF360"
              >
                <Checkbox
                  data-testid="cf360-dormant-filter-checkbox"
                  checked={includeCf360Dormant}
                  onCheckedChange={(v) => setIncludeCf360Dormant(!!v)}
                  className="h-3.5 w-3.5"
                />
                Include CF360 dormant
              </label>

              {/* v3.15-1058: Bulk Activate Selected — only visible in `only` mode with selections */}
              {isAdmin?.() && inactiveFilter === 'only' && selectedInactiveIds.size > 0 && (
                <Button
                  variant="default"
                  size="sm"
                  disabled={bulkActivating}
                  onClick={async () => {
                    if (!window.confirm(`Activate ${selectedInactiveIds.size} selected lead(s)? They will become visible to reps/dialer/queues. No emails or tasks will be dispatched.`)) return;
                    setBulkActivating(true);
                    try {
                      const res = await api.post('/admin/clients/bulk-activate', {
                        client_ids: Array.from(selectedInactiveIds),
                      });
                      const d = res.data || {};
                      toast.success(`Activated ${d.activated || 0} lead(s)${d.already_active ? ` (${d.already_active} already active)` : ''}${d.missing?.length ? ` · ${d.missing.length} not found` : ''}`);
                      setSelectedInactiveIds(new Set());
                      fetchClients();
                    } catch (e) {
                      toast.error(e?.response?.data?.detail || 'Bulk activate failed');
                    } finally {
                      setBulkActivating(false);
                    }
                  }}
                  data-testid="bulk-activate-button"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {bulkActivating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Activate Selected ({selectedInactiveIds.size})
                </Button>
              )}

              {/* v3.18-1387 CRM-001 — Bulk Transfer Rep. Uses the
                  generic per-row `selectedClients` checkboxes which
                  are visible for admins in all filter modes. */}
              {selectedClients.length > 0 && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setTransferBulk(true)}
                  data-testid="bulk-transfer-rep-button"
                  className="bg-slate-900 hover:bg-black text-white"
                >
                  <UserCog className="h-4 w-4 mr-1" />
                  Transfer Rep ({selectedClients.length})
                </Button>
              )}

              <Select value={stageFilter} onValueChange={setStageFilter}>
                <SelectTrigger className="w-full sm:w-48" data-testid="stage-filter">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Stage" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stages</SelectItem>
                  {stageOptions.map((stage) => (
                    <SelectItem key={stage.value} value={stage.value}>
                      {stage.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isInternal() && (
                <Select value={companyFilter} onValueChange={setCompanyFilter}>
                  <SelectTrigger className="w-full sm:w-48" data-testid="company-filter">
                    <Building2 className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Company" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Companies</SelectItem>
                    {externalCompanies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {/* v3.15-952-CLIENTS-TIMEZONE: time-zone filter (EST/CST/MST/PST). */}
              <Select value={tzFilter} onValueChange={setTzFilter}>
                <SelectTrigger className="w-full sm:w-44" data-testid="timezone-filter">
                  <Globe className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Time Zone" />
                </SelectTrigger>
                <SelectContent>
                  {TZ_FILTER_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* v3.15-974: VO workload UI (pills + Rebalance + dropdown)
                  REMOVED per spec — wrong placement and no operational
                  purpose on the Clients page. VO assignment, role logic,
                  and per-deal VO badges remain intact. */}

              {/* v3.15-952-WORK-QUEUE-VIEW: standard ⇄ work-queue toggle. */}
              <div
                className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50"
                data-testid="view-mode-toggle"
              >
                {[
                  { value: 'standard',   label: 'Standard',  testid: 'view-mode-standard' },
                  { value: 'work_queue', label: 'Work Queue', testid: 'view-mode-work-queue' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setViewMode(opt.value)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all flex items-center gap-1.5 ${
                      viewMode === opt.value
                        ? 'bg-white text-slate-900 shadow-sm'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                    data-testid={opt.testid}
                  >
                    {opt.value === 'work_queue' && <ListOrdered className="h-3.5 w-3.5" />}
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Results count */}
              <div className="flex items-center text-sm text-slate-500 ml-auto">
                {!loading && `${displayClients.length} client${displayClients.length !== 1 ? 's' : ''}`}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Clients List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500 mb-4">{error}</p>
              <Button 
                variant="outline" 
                onClick={() => {
                  setError(null);
                  fetchClients();
                }}
                data-testid="retry-load-clients"
              >
                Try Again
              </Button>
            </div>
          ) : displayClients.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-500">No clients found</p>
            </div>
          ) : (
            <div className="overflow-x-auto -mx-1 sm:mx-0">
              {/* Bulk Delete Action Bar */}
              {canDeleteClient && selectedClients.length > 0 && (
                <div className="flex items-center justify-between p-4 bg-red-50 border-b border-red-100">
                  <span className="text-sm font-medium text-red-800">
                    {selectedClients.length} client{selectedClients.length > 1 ? 's' : ''} selected
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedClients([])}
                      className="text-slate-600"
                    >
                      Clear Selection
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowBulkDeleteDialog(true)}
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="bulk-delete-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Selected
                    </Button>
                  </div>
                </div>
              )}
              {/* v3.21-1622 · Reduced column padding (px-6 → px-3) and
                  pinned the Actions column to the right so heavy
                  screens no longer push row controls off the visible
                  page.  See PRD v3.21-1622. */}
              <table className="w-full text-sm" data-testid="clients-table">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    {showRowCheckbox && (
                      <th className="py-3 px-3 w-10">
                        <Checkbox
                          checked={clients.length > 0 && selectedClients.length === clients.length}
                          onCheckedChange={toggleSelectAll}
                          className="border-slate-300"
                          data-testid="select-all-checkbox"
                        />
                      </th>
                    )}
                    <th className="text-left py-3 px-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="text-left py-3 px-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                      App ID
                    </th>
                    {isInternal() && (
                      <th className="text-left py-3 px-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                        Company
                      </th>
                    )}
                    <th className="text-left py-3 px-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                      Approved
                    </th>
                    <th className="text-left py-3 px-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                      Deal Amount
                    </th>
                    <th className="text-left py-3 px-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                      Sales Rep
                    </th>
                    {/* v3.18-1333 — VO column removed from Pipeline. */}
                    <th className="text-left py-3 px-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                      Stage
                    </th>
                    {/* v3.18-1333 — Course-corrected column order per
                        operator spec:  Client · App ID · Company ·
                        Approved · Deal Amount · Sales Rep · VO · Stage
                        · Next Action · Last Activity · Actions.
                        Rep Assist removed. */}
                    <th className="text-left py-3 px-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                      Next Action
                    </th>
                    <th className="text-left py-3 px-2 text-[10px] font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">
                      Last Activity
                    </th>
                    {showActionsColumn && (
                      <th className="text-right py-3 px-3 text-[10px] font-semibold text-slate-500 uppercase tracking-wider sticky right-0 z-10 bg-slate-50 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.08)]">
                        Actions
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {viewMode === 'work_queue' && workQueue ? (
                    <>
                      {/* Section 1 — Unassigned (always first) */}
                      {workQueue.unassigned.count > 0 && (
                        <>
                          <tr
                            className="bg-amber-50/60 border-y border-amber-200"
                            data-testid="work-queue-unassigned-header"
                          >
                            <td colSpan={isInternal() ? (showActionsColumn ? 11 : 9) : (showActionsColumn ? 10 : 8)} className="py-2 px-6">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-bold uppercase tracking-wider text-amber-800">
                                  Unassigned Clients (Needs Assignment)
                                </span>
                                <span className="text-[11px] text-amber-700">
                                  {workQueue.unassigned.count} client{workQueue.unassigned.count === 1 ? '' : 's'}
                                  {workQueue.unassigned.total > 0
                                    ? ` · $${workQueue.unassigned.total.toLocaleString()} potential`
                                    : ''}
                                </span>
                              </div>
                            </td>
                          </tr>
                          {workQueue.unassigned.items.map((client) => renderClientRow(client))}
                        </>
                      )}

                      {/* Section 2 — Assigned, grouped by Sales Rep (sorted by total $ desc) */}
                      {workQueue.reps.map((group) => (
                        <Fragment key={group.rep_id}>
                          <tr
                            className="bg-slate-50 border-y border-slate-200"
                            data-testid={`work-queue-rep-header-${group.rep_id}`}
                          >
                            <td colSpan={isInternal() ? (showActionsColumn ? 11 : 9) : (showActionsColumn ? 10 : 8)} className="py-2 px-6">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                                  {group.rep_name}
                                </span>
                                <span className="text-[11px] text-slate-500">
                                  {group.items.length} client{group.items.length === 1 ? '' : 's'}
                                  {group.total > 0 ? ` · $${group.total.toLocaleString()} pipeline` : ''}
                                </span>
                              </div>
                            </td>
                          </tr>
                          {group.items.map((client) => renderClientRow(client))}
                        </Fragment>
                      ))}
                    </>
                  ) : (
                    displayClients.map((client) => renderClientRow(client))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* v3.15-974: Rebalance Unassigned VO modal REMOVED with the
          VO workload UI. */}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!clientToDelete} onOpenChange={(open) => !open && setClientToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this client? This action cannot be undone.
              <div className="mt-3 p-3 bg-slate-50 rounded text-sm">
                <p><strong>Client:</strong> {clientToDelete?.full_name}</p>
                <p><strong>Email:</strong> {clientToDelete?.email}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteClient}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-client"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Client'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Delete Multiple Clients</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedClients.length}</strong> client{selectedClients.length > 1 ? 's' : ''}? 
              This action cannot be undone.
              <div className="mt-3 p-3 bg-red-50 rounded text-sm text-red-800 border border-red-200">
                <p className="font-medium mb-2">The following clients will be permanently deleted:</p>
                <ul className="list-disc pl-4 max-h-40 overflow-y-auto space-y-1">
                  {clients.filter(c => selectedClients.includes(c.id)).map(c => (
                    <li key={c.id}>{c.full_name} ({c.email})</li>
                  ))}
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-bulk-delete"
            >
              {bulkDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete {selectedClients.length} Client{selectedClients.length > 1 ? 's' : ''}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* v3.18-1387 CRM-001 — Rep Transfer dialog (single + bulk) */}
      <RepPickerDialog
        open={!!transferSingle}
        onClose={() => setTransferSingle(null)}
        mode="single"
        client={transferSingle}
        onTransferred={(result) => {
          setClients(prev => prev.map(c =>
            c.id === result.client_id
              ? { ...c, sales_rep_id: result.sales_rep_id,
                        sales_rep_name: result.sales_rep_name }
              : c));
        }}
      />
      <RepPickerDialog
        open={transferBulk}
        onClose={() => setTransferBulk(false)}
        mode="bulk"
        clientIds={selectedClients}
        onTransferred={(result) => {
          const okIds = new Set((result.transferred || []).map(r => r.client_id));
          setClients(prev => prev.map(c =>
            okIds.has(c.id)
              ? { ...c, sales_rep_id: result.new_rep?.id,
                        sales_rep_name: result.new_rep?.full_name }
              : c));
          setSelectedClients(prev => prev.filter(id => !okIds.has(id)));
        }}
      />
      {/* v3.21-1760 · Per-Client Financing Result workspace. State is
          scoped to `financingView` (one Client at a time), never
          global. Closing this modal only clears local state — it
          NEVER cancels upstream Fitnurf processing, which is owned by
          the Hub's `finturf_submissions` persistence + sync worker. */}
      <FinancingResultModal
        clientId={financingView?.id || null}
        clientName={financingView?.name || null}
        open={!!financingView}
        onClose={() => setFinancingView(null)}
      />
    </div>
  );
};

export default ClientsPage;
